"use strict";
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
var promise_polyfill_1 = __importDefault(require("promise-polyfill"));
var katal_metrics_1 = require("@amzn/katal-metrics");
var CacheRefresher_1 = require("./cache/CacheRefresher");
var ErrorHandler_1 = require("./ErrorHandler");
var cookieUtils_1 = require("./cookieUtils");
var DefaultCacheManagementSettings = {
    cacheRefreshRateSeconds: 600,
};
var KATAL_MONITORING_DIMENSION = { 'Katal Product': 'Katal Weblab' };
/**
 *  Main object responsible for interacting with weblabs
 */
var KatalWeblabClient = /** @class */ (function () {
    function KatalWeblabClient(options) {
        this.weblabProxy = options.weblabProxy;
        this.errorHandler = options.errorHandler || ErrorHandler_1.DEFAULT_ERROR_HANDLER;
        this.monitor = options.monitor;
        if (options.metricsPublisher) {
            this.metricsPublisher =
                options.metricsPublisher.newChildActionPublisherForMethod('KatalWeblab');
            // we do this check so that if a user updates this package but
            // doesn't use an updated proxy, it doesn't break
            if (this.weblabProxy.setMetricsPublisher) {
                this.weblabProxy.setMetricsPublisher(this.metricsPublisher);
            }
        }
        if (options.monitor) {
            this.monitor = options.monitor;
            if (this.weblabProxy.setMonitor) {
                this.weblabProxy.setMonitor(this.monitor);
            }
        }
        if (options.treatmentCache) {
            this.cache = options.treatmentCache;
            //Ensure all default values are present if a user does not configure everything
            this.cacheSettings = __assign(__assign({}, DefaultCacheManagementSettings), options.cacheBehavior);
            this.cacheRefresher = new CacheRefresher_1.CacheRefresher(options.treatmentCache, this.cacheSettings, this.weblabProxy, this.errorHandler, this.metricsPublisher);
            this.cacheRefresher.run();
        }
    }
    /**
     * Retrieves a single Weblab Treatment from the weblab client
     * @param weblab A weblab to retrieve a treatment for
     */
    KatalWeblabClient.prototype.getTreatment = function (weblab) {
        if (!weblab || !weblab.id || !weblab.defaultTreatment) {
            throw new Error('Invalid input: getTreatment must be passed a Weblab object');
        }
        return this.getTreatments([weblab]).then(function (result) { return result[weblab.id]; });
    };
    /**
     * Retrieves weblab treatments in batch from the weblab client
     * @param weblabs The array of weblabs to retrieve treatments for
     */
    KatalWeblabClient.prototype.getTreatments = function (weblabs) {
        if (!weblabs || !Array.isArray(weblabs)) {
            throw new Error('Invalid input: getTreatments must be passed an array of Weblab objects');
        }
        var treatmentPromise;
        if (this.cache) {
            treatmentPromise = this.loadTreatmentsFromCache(weblabs);
        }
        else {
            treatmentPromise = this.getTreatmentsOrFallbackToDefaultValues(weblabs);
        }
        var result = {};
        return treatmentPromise.then(function (treatments) {
            treatments.forEach(function (t) {
                result[t.weblabId] = t;
            });
            return result;
        });
    };
    /**
     * Records a trigger for a cached weblab
     *
     * this is the same function as the trigger that exists on the Treatment interface
     *
     * @param weblab the weblab to record a trigger for
     * @param treatment the treatment value of the weblab to record a trigger for
     */
    KatalWeblabClient.prototype.triggerWeblab = function (weblab, treatment) {
        // Related to SIM - https://sim.amazon.com/issues/KAT-8685
        // prior to this line, this weblab proxy would allow recording null/falsy
        // triggers. As noted in the SIM, the intent of recording a null trigger is
        // "is incorrect and should be blocked here"
        // https://sim.amazon.com/issues/KAT-8685?selectedConversation=2c87938b-179d-4fb7-b7e4-75d4c2a03587
        var _this = this;
        var _a, _b, _c;
        // Weblab Proxies may choose to set `assignmentMissing` on a treatment when Weblab returns `null` as a treatment value.
        // This prevents a breaking change when differentiating between `null` and `defaultTreatment` cases, but a future
        // major version will properly account for `null` treatments.
        if (!((_a = treatment) === null || _a === void 0 ? void 0 : _a.treatmentValue) || ((_b = treatment) === null || _b === void 0 ? void 0 : _b.assignmentMissing)) {
            return promise_polyfill_1.default.resolve();
        }
        var METRIC_NAME = 'RecordTrigger.Promise';
        var metricAttempt = new katal_metrics_1.Metric.TimedAttempt(METRIC_NAME).withMonitor();
        var monitorAttempt = (_c = this.monitor) === null || _c === void 0 ? void 0 : _c.recordStatMetrics(METRIC_NAME, KATAL_MONITORING_DIMENSION);
        return this.weblabProxy
            .recordTrigger(weblab, treatment.treatmentValue)
            .then(function () {
            var _a;
            metricAttempt.setSuccess();
            _this.publishMetricsIfExists(function (p) { return p.publish(metricAttempt); });
            (_a = monitorAttempt) === null || _a === void 0 ? void 0 : _a.success();
        })
            .catch(function (err) {
            var _a;
            metricAttempt.setFailure();
            _this.publishMetricsIfExists(function (p) { return p.publish(metricAttempt); });
            (_a = monitorAttempt) === null || _a === void 0 ? void 0 : _a.fault();
            throw err;
        });
    };
    KatalWeblabClient.prototype.loadTreatmentsFromCache = function (weblabs) {
        var _this = this;
        var METRIC_NAME = 'TreatmentsCacheHit';
        return new promise_polyfill_1.default(function (resolve) {
            var _a, _b;
            var result = _this.getTreatmentsFromCache(weblabs);
            var foundTreatments = result.foundTreatments;
            var missingTreatments = result.missingTreatments;
            // we are fully cached, return the results
            if (!missingTreatments.length) {
                _this.publishMetricsIfExists(function (p) {
                    p.publishCounterMonitor(METRIC_NAME, 1);
                });
                (_a = _this.monitor) === null || _a === void 0 ? void 0 : _a.count(METRIC_NAME, 1, KATAL_MONITORING_DIMENSION);
                resolve(foundTreatments);
                return;
            }
            _this.publishMetricsIfExists(function (p) {
                p.publishCounterMonitor(METRIC_NAME, 0);
            });
            (_b = _this.monitor) === null || _b === void 0 ? void 0 : _b.count(METRIC_NAME, 0, KATAL_MONITORING_DIMENSION);
            //in this case we have to make a call for some of the specified treatments
            //its a batch call, might as well get everything
            var results = _this.getTreatmentsOrFallbackToDefaultValues(weblabs);
            results.then(function (weblabs) {
                resolve(weblabs);
            });
        });
    };
    /**
     * Determines which weblabs we have stored in our cache
     * and which values are missing
     */
    KatalWeblabClient.prototype.getTreatmentsFromCache = function (weblabs) {
        var _this = this;
        var foundTreatments = [];
        var missingTreatments = [];
        var weblabCookie = cookieUtils_1.readWeblabCookie();
        weblabs.forEach(function (w) {
            // if the weblabId is in the weblab cookie, we bypass the cache
            var treatment = w.id in weblabCookie ? undefined : _this.cache.getTreatment(w.id);
            if (!treatment) {
                missingTreatments.push(_this.convertWeblabToDefaultTreatment(w));
            }
            else {
                foundTreatments.push(_this.convertCachableTreatmentToTreatment(w, treatment));
            }
        });
        return { foundTreatments: foundTreatments, missingTreatments: missingTreatments };
    };
    KatalWeblabClient.prototype.publishMetricsIfExists = function (callback) {
        if (this.metricsPublisher) {
            callback(this.metricsPublisher);
        }
    };
    /**
     * Converts a cached treatment into a full treatment that's able
     * to record triggers
     */
    KatalWeblabClient.prototype.convertCachableTreatmentToTreatment = function (weblab, treatment) {
        var _this = this;
        return __assign(__assign({}, treatment), { recordTrigger: function () { return _this.triggerWeblab(weblab, treatment); } });
    };
    /**
     * Calls the WeblabClient to return treatments to an application
     * If any errors are encountered communicating with the weblab client
     * fallback to the default values specified by the application
     */
    KatalWeblabClient.prototype.getTreatmentsOrFallbackToDefaultValues = function (weblabs) {
        var _this = this;
        var _a;
        var METRIC_NAME = 'GetTreatments.Promise';
        var metricAttempt = new katal_metrics_1.Metric.TimedAttempt(METRIC_NAME).withMonitor();
        var monitorAttempt = (_a = this.monitor) === null || _a === void 0 ? void 0 : _a.recordStatMetrics(METRIC_NAME, KATAL_MONITORING_DIMENSION);
        return this.weblabProxy
            .getTreatments(weblabs)
            .then(function (mapping) {
            var _a;
            metricAttempt.setSuccess();
            _this.publishMetricsIfExists(function (publisher) {
                publisher.publish(metricAttempt);
            });
            (_a = monitorAttempt) === null || _a === void 0 ? void 0 : _a.success();
            var treatments = [];
            mapping.forEach(function (v) {
                treatments.push(v);
            });
            _this.updateCache(mapping);
            return treatments;
        })
            .catch(function (error) {
            var _a;
            metricAttempt.setFailure();
            _this.publishMetricsIfExists(function (publisher) {
                publisher.publish(metricAttempt);
            });
            (_a = monitorAttempt) === null || _a === void 0 ? void 0 : _a.fault();
            _this.errorHandler(error);
            return weblabs.map(function (weblab) {
                return _this.convertWeblabToDefaultTreatment(weblab);
            });
        });
    };
    KatalWeblabClient.prototype.convertWeblabToDefaultTreatment = function (weblab) {
        return this.convertCachableTreatmentToTreatment(weblab, {
            treatmentValue: weblab.defaultTreatment,
            weblabId: weblab.id,
            // https://issues.amazon.com/issues/KAT-12099
            assignmentMissing: true,
        });
    };
    /**
     * Updates the cache if it exists with the given weblab values
     */
    KatalWeblabClient.prototype.updateCache = function (treatments) {
        var _this = this;
        if (!this.cache) {
            return;
        }
        treatments.forEach(function (treatment, weblab) {
            _this.cache.addTreatment(treatment, weblab);
        });
    };
    return KatalWeblabClient;
}());
exports.KatalWeblabClient = KatalWeblabClient;
